import type { DisciplineStageComment, DisciplineStageCommentPayload } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface DisciplineStageCommentStoreApiOptions {
  disciplineId: string | number
  payload: DisciplineStageCommentPayload
  onStart?: () => void
  onFinish?: () => void
}

interface DisciplineStageCommentUpdateApiOptions extends DisciplineStageCommentStoreApiOptions {
  commentId: number
}

const INVALID_URL = 'Unexpected error. Invalid request URL.'

/** `POST …/stage-comments` — returns the new row with the caller as its agent. */
export const useDisciplineStageCommentStoreApi = async (
  options: DisciplineStageCommentStoreApiOptions,
): Promise<DisciplineStageComment | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const url = useAdminRoutes('admin.disciplines.api.stage-comments', { discipline_id: options.disciplineId })

  options.onStart?.()

  try {
    if (!url) {
      toast.error(INVALID_URL)
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, options.payload)

    toast.success('Comment was added successfully.')

    return data.data ?? null
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    options.onFinish?.()
  }
}

/** `PATCH …/stage-comments/{comment}` — the whole text goes back; null channel and reach clear them. */
export const useDisciplineStageCommentUpdateApi = async (
  options: DisciplineStageCommentUpdateApiOptions,
): Promise<DisciplineStageComment | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const url = useAdminRoutes('admin.disciplines.api.stage-comment', {
    discipline_id: options.disciplineId,
    comment_id: options.commentId,
  })

  options.onStart?.()

  try {
    if (!url) {
      toast.error(INVALID_URL)
      return null
    }

    const { data } = await $axios.patch<ApiResponse>(url, options.payload)

    toast.success('Comment was updated successfully.')

    return data.data ?? null
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    options.onFinish?.()
  }
}
